import React, { useState } from 'react';
import { X, BookOpen, Loader2, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { apiPost } from '@/utils/api';

interface ElementInput {
  id: string;
  name: string;
  weight: number;
  volumeHoraire: number | null;
}

interface CreateModuleModalProps {
  semesterId: string;
  onClose: () => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
}

// Generate unique ID
const generateId = () => Math.random().toString(36).substr(2, 9);

const CreateModuleModal: React.FC<CreateModuleModalProps> = ({
  semesterId,
  onClose,
  onSuccess,
  onError,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    coefficient: 1,
  });
  const [elements, setElements] = useState<ElementInput[]>([
    { id: generateId(), name: '', weight: 100, volumeHoraire: null }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'coefficient' ? parseFloat(value) || 1 : value,
    }));
  };

  // Add element
  const addElement = () => {
    const newElements = [...elements, { id: generateId(), name: '', weight: 0, volumeHoraire: null }];
    // Redistribute weights
    const weightPerElement = Math.floor(100 / newElements.length);
    const remainder = 100 - weightPerElement * newElements.length;
    setElements(newElements.map((el, idx) => ({
      ...el,
      weight: weightPerElement + (idx === 0 ? remainder : 0),
    })));
  };

  // Remove element
  const removeElement = (elementId: string) => {
    if (elements.length <= 1) return;
    const newElements = elements.filter(el => el.id !== elementId);
    // Redistribute weights
    const weightPerElement = Math.floor(100 / newElements.length);
    const remainder = 100 - weightPerElement * newElements.length;
    setElements(newElements.map((el, idx) => ({
      ...el,
      weight: weightPerElement + (idx === 0 ? remainder : 0),
    })));
  };

  // Update element
  const updateElement = (elementId: string, field: keyof ElementInput, value: string | number | null) => {
    setElements(prev => prev.map(el =>
      el.id === elementId ? { ...el, [field]: value } : el
    ));
  };

  // Calculate total weight
  const getTotalWeight = () => {
    return elements.filter(el => el.name.trim()).reduce((sum, el) => sum + el.weight, 0);
  };

  // Validate form
  const validateForm = (): string | null => {
    if (!formData.code.trim()) return 'Le code du module est requis';
    if (!formData.name.trim()) return 'Le nom du module est requis';
    if (formData.coefficient < 0.1) return 'Le coefficient doit être >= 0.1';

    const validElements = elements.filter(el => el.name.trim());
    if (validElements.length === 0) return 'Au moins un élément est requis';

    const totalWeight = validElements.reduce((sum, el) => sum + el.weight, 0);
    if (Math.abs(totalWeight - 100) > 0.1) {
      return `Les poids des éléments doivent totaliser 100% (actuellement: ${totalWeight}%)`;
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      // Use bulk endpoint to create module with elements
      const bulkData = {
        semesterId,
        modules: [{
          code: formData.code.trim(),
          name: formData.name.trim(),
          coefficient: formData.coefficient,
          elements: elements
            .filter(el => el.name.trim())
            .map(el => ({
              name: el.name.trim(),
              weight: el.weight,
              volumeHoraire: el.volumeHoraire,
            })),
        }],
      };

      await apiPost('/filieres/modules/bulk', bulkData);

      onSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la création';
      setError(message);
      onError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalWeight = getTotalWeight();
  const isWeightValid = Math.abs(totalWeight - 100) <= 0.1;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Nouveau Module</h2>
                <p className="text-sm text-gray-500">Ajouter un module avec ses éléments</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
              {/* Module Info */}
              <div className="grid grid-cols-3 gap-4">
                {/* Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    placeholder="POO101"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  />
                </div>

                {/* Nom */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du Module <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Programmation Orientée Objet"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  />
                </div>
              </div>

              {/* Coefficient */}
              <div className="w-32">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Coefficient <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="coefficient"
                  value={formData.coefficient}
                  onChange={handleChange}
                  step="0.5"
                  min="0.5"
                  max="10"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                />
              </div>

              {/* Elements Section */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-700">Éléments de module</h3>
                  <div className={`text-xs font-medium px-2 py-1 rounded ${
                    isWeightValid ? 'text-green-600 bg-green-50' : 'text-amber-600 bg-amber-50'
                  }`}>
                    Total: {totalWeight}% {!isWeightValid && '(doit être 100%)'}
                  </div>
                </div>

                {/* Elements Header */}
                <div className="grid grid-cols-[1fr_80px_100px_32px] gap-2 mb-2 text-xs text-gray-500 font-medium">
                  <span>Nom de l'élément</span>
                  <span className="text-center">Poids (%)</span>
                  <span className="text-center">Vol. horaire (h)</span>
                  <span></span>
                </div>

                {/* Elements List */}
                <div className="space-y-2">
                  {elements.map((element, idx) => (
                    <div key={element.id} className="grid grid-cols-[1fr_80px_100px_32px] gap-2 items-center">
                      <input
                        type="text"
                        value={element.name}
                        onChange={(e) => updateElement(element.id, 'name', e.target.value)}
                        placeholder={`Élément ${idx + 1}`}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                      <input
                        type="number"
                        value={element.weight}
                        onChange={(e) => updateElement(element.id, 'weight', parseFloat(e.target.value) || 0)}
                        min={0}
                        max={100}
                        className="w-full px-2 py-2 text-sm text-center border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                      <input
                        type="number"
                        value={element.volumeHoraire ?? ''}
                        onChange={(e) => updateElement(element.id, 'volumeHoraire', e.target.value ? parseFloat(e.target.value) : null)}
                        min={0}
                        step={0.5}
                        placeholder="Ex: 30"
                        className="w-full px-2 py-2 text-sm text-center border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                      <button
                        type="button"
                        onClick={() => removeElement(element.id)}
                        disabled={elements.length <= 1}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add Element Button */}
                <button
                  type="button"
                  onClick={addElement}
                  className="mt-3 flex items-center gap-1 px-3 py-1.5 text-xs text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Ajouter un élément
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !isWeightValid}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Création...
                  </>
                ) : (
                  'Créer le Module'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateModuleModal;
